import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';

/** Shape of GET /api/auth/me (FRONT-145 adds `isAdmin`; FRONT-481 `isSuperAdmin`). */
export interface AuthMe {
  authenticated: boolean;
  userId?: string;
  expiresAt?: number;
  name?: string;
  email?: string;
  isAdmin?: boolean;
  /** kb:superadmin for the acting org — the cross-org tier (a superset of isAdmin). */
  isSuperAdmin?: boolean;
  /** FRONT-1450: may this user CREATE apps? False only for the consumer tier. */
  canBuildApps?: boolean;
  /** FRONT-2731: may this user create/edit custom agents? Narrower than
   *  `canBuildApps` — a Pro User builds apps but may not author agents. */
  canAuthorCustomAgents?: boolean;
}

/**
 * Read the admin flag off the shared `['auth','me']` boot query (FRONT-145).
 *
 * Reuses the exact query key the app-layout auth gate already populates, so
 * this adds no extra request — TanStack returns the cached `/api/auth/me`
 * result. Admin UI must treat `isLoading` as "not admin yet" and fail closed:
 * `isAdmin` is only true once the boot query has resolved with the flag set,
 * so there is no flash of admin controls while auth is still loading.
 */
export function useAuthMe() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        return await api.get<AuthMe>('/api/auth/me');
      } catch (err) {
        if ((err as { status?: number } | null)?.status === 401) {
          return { authenticated: false } as AuthMe;
        }
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

/**
 * Current user identity off the same cached `['auth','me']` boot query — no
 * extra request. Used to gate owner-only actions (FRONT-649): compare
 * `userId` against a resource's `createdByZitadelUserId`.
 */
export function useCurrentUser(): {
  userId: string | undefined;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isLoading: boolean;
} {
  const query = useAuthMe();
  return {
    userId: query.data?.userId,
    isAdmin: query.data?.isAdmin === true,
    isSuperAdmin: query.data?.isSuperAdmin === true,
    isLoading: query.isLoading,
  };
}

export function useIsAdmin(): { isAdmin: boolean; isLoading: boolean } {
  const query = useAuthMe();
  return {
    isAdmin: query.data?.isAdmin === true,
    isLoading: query.isLoading,
  };
}

/**
 * Read the super-admin flag off the same `['auth','me']` boot query (FRONT-481).
 * Fails closed exactly like {@link useIsAdmin}: only true once the boot query
 * resolves with `isSuperAdmin`, so no flash of super-admin controls while auth
 * is still loading.
 */
export function useSuperAdmin(): { isSuperAdmin: boolean; isLoading: boolean } {
  const query = useAuthMe();
  return {
    isSuperAdmin: query.data?.isSuperAdmin === true,
    isLoading: query.isLoading,
  };
}

/**
 * FRONT-1450: may the current user create apps (start a build, publish)? Read
 * off the same cached `['auth','me']` boot query — no extra request.
 *
 * Fails SAFE in the opposite direction to `useIsAdmin`: while auth is loading,
 * or against a server that predates the field, this returns TRUE. Hiding the
 * builder UI from someone who is allowed to build (a flash of "ask your admin"
 * on every page load, or a permanent block after a rollback) is a worse failure
 * than briefly showing a button the server would refuse anyway — the 403 is the
 * real gate; this only keeps the UI honest.
 */
export function useCanBuildApps(): { canBuildApps: boolean; isLoading: boolean } {
  const query = useAuthMe();
  return {
    canBuildApps: query.data?.canBuildApps !== false,
    isLoading: query.isLoading,
  };
}

/**
 * FRONT-2731: may the current user author custom agents (create, edit,
 * delete, revert)? Read off the same `['auth','me']` boot query. The server
 * derives it from the very helper its custom-agent write routes gate on, so
 * the create button and the save can no longer disagree. Fails open only
 * while the query is loading or against an old server that predates the field
 * (same as {@link useCanBuildApps}). A user with no roles gets FALSE from the
 * server on purpose: the workspace refuses their save anyway, so the UI says so
 * up front. The server's refusal stays the real gate.
 */
export function useCanAuthorCustomAgents(): { canAuthorCustomAgents: boolean; isLoading: boolean } {
  const query = useAuthMe();
  return {
    canAuthorCustomAgents: query.data?.canAuthorCustomAgents !== false,
    isLoading: query.isLoading,
  };
}
